import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { OidcSecurityService, OpenIdConfiguration } from 'angular-auth-oidc-client';
import { take, takeUntil } from 'rxjs/operators';

import { environment } from 'environments/environment';

import { ComponentBase } from '../component.base';
import { UserProfile } from '../models/user-profile.models';
import { GtmEventsService } from './gtm-events.service';

@Injectable({
	providedIn: 'root',
})
export class AuthService extends ComponentBase {
	openIdConfiguration: OpenIdConfiguration;
	token: string = '';
	isAuthorized = false;
	userData: UserProfile;

	private get basePortalUrl(): string {
		return environment.basePortalUrl;
	}

	private get authUrl(): string {
		return environment.authUrl;
	}

	constructor(
		private oidcSecurityService: OidcSecurityService,
		private router: Router,
		private gtmEventsService: GtmEventsService,
	) {
		super();
	}

	public initAuth() {
		this.openIdConfiguration = {
			configId: 'auth',
			authority: this.authUrl,
			redirectUrl: `${this.basePortalUrl}/auth-callback`,
			clientId: 'marketplace_client',
			responseType: 'code',
			scope: 'openid profile email offline_access wine_portal_api',
			postLogoutRedirectUri: this.basePortalUrl,
			forbiddenRoute: '/forbidden',
			unauthorizedRoute: '/unauthorized',
			silentRenew: true,
			silentRenewUrl: `${this.basePortalUrl}/silent-renew.html`,
			historyCleanupOff: true,
			autoUserInfo: true,
			maxIdTokenIatOffsetAllowedInSeconds: 298,
			// maxIdTokenIatOffsetAllowedInSeconds: 298, // Matt L has said to disable this check.
			disableIatOffsetValidation: true,
			authWellknownEndpoints: {
				issuer: this.authUrl,
				jwksUri: `${this.authUrl}/.well-known/openid-configuration/jwks`,
				authorizationEndpoint: `${this.authUrl}/connect/authorize`,
				tokenEndpoint: `${this.authUrl}/connect/token`,
				userInfoEndpoint: `${this.authUrl}/connect/userinfo`,
				endSessionEndpoint: `${this.authUrl}/connect/endsession`,
				checkSessionIframe: `${this.authUrl}/connect/checksession`,
				revocationEndpoint: `${this.authUrl}/connect/revocation`,
				introspectionEndpoint: `${this.authUrl}/connect/introspect`,
			},
		};
		this.checkAuth();
	}

	checkAuth(): void {
		this.oidcSecurityService.checkAuth('', 'auth').subscribe(({ isAuthenticated, accessToken, userData }) => {
			this.oidcSecurityService
				.getAccessToken('auth')
				.pipe(takeUntil(this.ngUnsubscribe$))
				.subscribe({
					next: (token) => {
						this.token = token;
					},
				});
			if (isAuthenticated) {
				this.mapUserData(userData);
				this.gtmEventsService.loginEvent(this.userData.account_id);
				this.goToReturnUrl();
			}
		});
	}

	login(): void {
		if (this.router.url.indexOf('/auth-callback') === -1) {
			localStorage.setItem('returnUrl', window.location.pathname);
			this.oidcSecurityService.authorize();
		}
	}

	loginIfRequired(): void {
		this.oidcSecurityService.isAuthenticated$.pipe(take(1)).subscribe({
			next: (status) => {
				if (!status.isAuthenticated) {
					localStorage.setItem('returnUrl', window.location.pathname);
					this.oidcSecurityService.authorize();
				} else {
					this.goToReturnUrl();
				}
			},
		});
	}

	logout(): void {
		this.oidcSecurityService.logoff().subscribe((result) => {
			this.gtmEventsService.logoutEvent();
		});
	}

	private mapUserData(userData: any) {
		if (userData) {
			this.userData = userData;
		}
	}

	private goToReturnUrl(): void {
		if (localStorage.getItem('returnUrl') != null) {
			const returnUrl = localStorage.getItem('returnUrl');
			localStorage.removeItem('returnUrl');
			this.router.navigate([returnUrl]);
		} else {
			// this.router.navigate([]);
		}
	}
}
